import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react'

import { filterSuggestions } from '../screens/catalogFilters'

export type SelectOption = {
  value: string
  label: string
  /** Небольшая иконка у действия в выпадающем списке. */
  icon?: ReactNode
}

type SelectComboboxProps = {
  label: string
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  /** Показывать весь список при фокусе (как «Категория» в каталоге). */
  listAllOnFocus?: boolean
  maxOptions?: number
  inputClassName?: string
  required?: boolean
  name?: string
  hideLabel?: boolean
  /** Родитель уже рисует label — только input + список. */
  embedded?: boolean
  /** Открывать список над полем, когда снизу нет места. */
  menuPlacement?: 'top' | 'bottom'
}

export const SelectCombobox = ({
  label,
  value,
  options,
  onChange,
  placeholder = 'Выберите…',
  className,
  disabled = false,
  listAllOnFocus = true,
  maxOptions = 200,
  inputClassName = 'echat-field__input',
  required = false,
  name,
  hideLabel = false,
  embedded = false,
  menuPlacement = 'bottom',
}: SelectComboboxProps) => {
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  )

  const syncQueryFromValue = () => {
    setQuery(selected?.label ?? '')
  }

  useEffect(() => {
    if (!open) {
      syncQueryFromValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only follow selected label when closed
  }, [selected, open])

  const filteredOptions = useMemo(() => {
    const trimmed = query.trim()
    const selectedLabel = selected?.label ?? ''
    // While browsing an already selected value, show the full list (like a native select).
    const browsingSelected = trimmed.length === 0 || trimmed === selectedLabel
    if (browsingSelected && listAllOnFocus) {
      return options.slice(0, maxOptions)
    }
    if (!trimmed) {
      return []
    }
    const labels = filterSuggestions(
      options.map((option) => option.label),
      trimmed,
      maxOptions,
    )
    const byLabel = new Map(options.map((option) => [option.label, option]))
    return labels
      .map((optionLabel) => byLabel.get(optionLabel))
      .filter((option): option is SelectOption => option != null)
  }, [listAllOnFocus, maxOptions, options, query, selected])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [])

  const showList = open && !disabled
  const selectedLabel = selected?.label ?? ''
  const browsingSelected = !query.trim() || query.trim() === selectedLabel
  const hasQuery = query.trim().length > 0 && !browsingSelected

  const handlePick = (option: SelectOption) => {
    onChange(option.value)
    setQuery(option.label)
    setOpen(false)
  }

  const input = (
    <input
      className={inputClassName}
      name={name}
      value={query}
      placeholder={placeholder}
      disabled={disabled}
      required={required && !value}
      role="combobox"
      aria-label={embedded || hideLabel ? label : undefined}
      aria-expanded={showList}
      aria-controls={listId}
      aria-autocomplete="list"
      onChange={(event) => {
        const next = event.target.value
        setQuery(next)
        setOpen(true)
        if (!next.trim()) {
          onChange('')
          return
        }
        const exact = options.find(
          (option) => option.label.toLocaleLowerCase('ru') === next.trim().toLocaleLowerCase('ru'),
        )
        if (exact) {
          onChange(exact.value)
        }
      }}
      onFocus={() => setOpen(true)}
      onClick={() => setOpen(true)}
    />
  )

  return (
    <div
      ref={rootRef}
      className={['filter-combobox', className].filter(Boolean).join(' ')}
    >
      {embedded ? (
        input
      ) : (
        <label className="echat-field">
          {hideLabel ? <span className="visually-hidden">{label}</span> : (
            <span className="echat-field__label">{label}</span>
          )}
          {input}
        </label>
      )}
      {showList ? (
        <ul
          id={listId}
          className={`filter-combobox__list${menuPlacement === 'top' ? ' filter-combobox__list--top' : ''}`}
          role="listbox"
        >
          {!hasQuery && !listAllOnFocus ? (
            <li className="filter-combobox__hint">Введите название для поиска</li>
          ) : null}
          {hasQuery && filteredOptions.length === 0 ? (
            <li className="filter-combobox__hint">Ничего не найдено</li>
          ) : null}
          {filteredOptions.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                className="filter-combobox__option"
                role="option"
                aria-selected={option.value === value}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handlePick(option)}
              >
                {option.icon ? <span className="filter-combobox__option-icon">{option.icon}</span> : null}
                <span>{option.label}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
